import { Button, Form, Input, Radio, Select, Skeleton } from 'antd';
import { useContext, useMemo } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { RedirectIcon } from 'app/components/pages/Hotels/RedirectIcon';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { usePackages } from 'app/hooks/data/packages/usePackages';
import { useAdminUser } from 'app/hooks/data/useAdminUser';
import type { OpeningHours } from 'app/typings/core/admin/packs/openingHours/openingHours';
import { formatPackageName } from 'app/utils/packages';

const { Option } = Select;
const RULES = [{ required: true, message: 'Required' }];

export const BasicInformations = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const { data: admins } = useAdminUser();

  const form = Form.useFormInstance<{
    slug: string;
    isDaytime: boolean;
    aliasForUser: string;
    openingHours: OpeningHours;
    oldPackId?: number;
  }>();
  const formValues = Form.useWatch([], form);

  const { data: listODMPackages, isFetching } = usePackages(
    formValues?.isDaytime,
    formValues?.isDaytime !== undefined ? true : false
  );

  const formattedPackages = useMemo(
    () =>
      listODMPackages?.map((pkg) => ({
        id: pkg.id,
        name: formatPackageName({
          name: pkg.aliasForHotel,
          hotelName: pkg.hotelName,
        }),
        coverPicture: pkg.coverPicture,
        searchName: pkg.aliasForUser ?? pkg.aliasForHotel,
      })) ?? [],
    [listODMPackages]
  );

  const { data: hotels } = useHotels();

  if (!hotels) {
    return null;
  }

  return (
    <>
      <SCard title="Basic Informations" className="pack-form">
        <FormLineLayout label="Type">
          <Form.Item name="isDaytime" rules={RULES}>
            <Radio.Group disabled>
              <Radio value={false}>Night stay</Radio>
              <Radio value={true}>Daytime</Radio>
            </Radio.Group>
          </Form.Item>
        </FormLineLayout>

        <FormLineLayout label="Hotel">
          <Form.Item name="hotelId" rules={RULES}>
            <Select
              placeholder="Select a hotel"
              showSearch
              optionFilterProp="children"
              disabled
              className="select-long-width"
            >
              {hotels.map((hotel) => (
                <Option key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FormLineLayout>

        {formValues?.slug && (
          <FormLineLayout label="Slug">
            <Form.Item name="slug" rules={RULES}>
              <Input disabled />
            </Form.Item>
          </FormLineLayout>
        )}

        {formValues?.isDaytime && (
          <FormLineLayout label="Owner" isRequired>
            <Form.Item name="ownerId" rules={RULES}>
              <Select
                showSearch
                disabled={!isEditMode}
                placeholder={isEditMode ? 'Select an owner' : undefined}
                optionFilterProp="children"
                className="select-long-width"
                filterOption={(input, option) => {
                  return (
                    option?.key.toLowerCase().indexOf(input.toLowerCase()) >=
                      0 ||
                    option?.title.toLowerCase().indexOf(input.toLowerCase()) >=
                      0
                  );
                }}
              >
                {admins?.map((admin) => (
                  <Option
                    title={`${admin.firstName} ${admin.lastName}`}
                    key={admin.email}
                    id={admin.id}
                    value={admin.id}
                  >
                    {admin.firstName && admin.lastName
                      ? `${admin.firstName} ${admin.lastName} - ${admin.email}`
                      : `${admin.email}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </FormLineLayout>
        )}
      </SCard>
      <SCard className="pack-form">
        <FormLineLayout
          label="ODM package"
          isOptional
          description={
            <>
              Use only when recreating a package from the Old Data Model, in
              order to link bookings and reviews history.
              <br />
              If this is a brand new package, leave empty.
            </>
          }
        >
          <Form.Item name="oldPackId">
            {isFetching ? (
              <Skeleton.Input
                active={true}
                size="default"
                style={{ width: '400px' }}
              />
            ) : (
              <Select
                showSearch={true}
                optionFilterProp="children"
                disabled={!isEditMode}
                placeholder="Select a package from the list"
                className="select-long-width"
                filterOption={(input, option) =>
                  option?.props.key
                    .toLowerCase()
                    .toString()
                    .includes(input.toLowerCase())
                }
              >
                {formattedPackages.map((data) => (
                  <Option
                    key={`${data.name}-${data.id}`}
                    value={data.id}
                    className="select-item__option"
                  >
                    <div className="select-item__wrapper">
                      {data.coverPicture && (
                        <div className="select-item__thumbnail">
                          <PictureComponent {...data.coverPicture} />
                        </div>
                      )}
                      <div className="select-item__name">{data.name}</div>
                    </div>
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {!isEditMode && !!formValues?.oldPackId && (
            <Button
              icon={<RedirectIcon />}
              href={`/packs/legacy/${formValues.oldPackId}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
        </FormLineLayout>
      </SCard>
    </>
  );
};
